import React, { useState } from 'react';
import {
  LayoutDashboard,
  Package,
  FileText,
  Brain,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronRight,
  Home,
  Wrench,
  DollarSign,
  Users,
  Search,
  TrendingUp,
  FileBarChart,
  Cog,
  Calendar,
  Shield,
  Link,
  MessageSquare,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileCheck,
  Zap,
  Globe,
  Mic,
  Languages,
  Users2,
  Calculator,
  Receipt,
  CreditCard,
  PieChart,
  Target,
  Building,
  Building2,
  UserCheck,
  Lock,
  Database,
  Cloud,
  Smartphone,
  Monitor,
  FolderOpen,
  Bell,
  Workflow,
  Upload,
  History,
  Settings2,
  Wifi,
  WifiOff,
  MessageCircle,
  Lightbulb,
  BookOpen,
  Headphones,
  ClipboardList,
  Send
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAI } from '../../context/AIContext';
import CAM360Badge from '../common/CAM360Badge';

// Theme colors aligned with theme.css sidebar tokens
const SIDEBAR_THEME = {
  bg: '#1c2333',
  footerBg: '#151b29',
  borderColor: 'rgba(116, 134, 214, 0.12)',
  textMuted: '#8b9ab8',
  textColor: '#c8d0e7',
  activeColor: '#7486d6',
  activeBg: 'rgba(116, 134, 214, 0.12)',
  hoverBg: 'rgba(116, 134, 214, 0.08)',
  separatorGradient: 'linear-gradient(90deg, transparent 0%, rgba(116, 134, 214, 0.10) 20%, rgba(116, 134, 214, 0.18) 50%, rgba(116, 134, 214, 0.10) 80%, transparent 100%)',
  disabledColor: '#4a5568',
};

const EnterpriseNavigation = ({ activePage, onNavigate, isOpen = true }) => {
  const { user } = useAuth();

  const [expandedGroups, setExpandedGroups] = useState({
    overview: true,
    assets: true,
    contracts: true,
    ai: false,
    finance: false,
    integration: false,
    collaboration: false,
    insights: false,
    system: false,
  });

  const menuGroups = [
  // 1. Overview
  {
    id: 'overview',
    name: 'Overview',
    icon: <LayoutDashboard size={20} />,
    color: '#7486d6',
    items: [
      { id: 'dashboard', name: 'Dashboard', icon: <Home size={18} />, path: '/dashboard', available: true },
      { id: 'reports', name: 'Reports', icon: <FileBarChart size={18} />, path: '/insights/reports', available: true, subtitle: 'comprehensive reporting suite' },
      { id: 'dashboards', name: 'Dashboards', icon: <PieChart size={18} />, path: '/insights/dashboards', available: true, subtitle: 'real-time KPIs & metrics' },
      { id: 'analytics', name: 'Analytics', icon: <TrendingUp size={18} />, path: '/insights/analytics', available: true, subtitle: 'advanced data analytics' },
    ],
  },

  // 2. Sourcing
  {
    id: 'sourcing',
    name: 'Sourcing',
    icon: <ClipboardList size={20} />,
    color: '#94cee4',
    items: [
      { id: 'rfp-management', name: 'RFP Management', icon: <FileText size={18} />, path: '/sourcing/rfps', available: true, subtitle: 'create, publish & manage RFPs' },
      { id: 'vendor-responses', name: 'Vendor Responses', icon: <Users size={18} />, path: '/sourcing/responses', available: true, subtitle: 'view supplier submissions' },
      { id: 'bid-evaluation', name: 'Bid Evaluation', icon: <BarChart3 size={18} />, path: '/sourcing/evaluation-award?tab=evaluation', available: true, subtitle: 'technical & commercial scoring' },
      { id: 'awards', name: 'Awards', icon: <CheckCircle size={18} />, path: '/sourcing/evaluation-award?tab=award', available: true, subtitle: 'approve vendors & create contracts' },
      { id: 'sourcing-e-signature', name: 'E-Signature', icon: <FileCheck size={18} />, path: '/sourcing/e-signature', available: true, subtitle: 'digital signature management' },
    ],
  },

  // 3. Contracts
  {
    id: 'contracts',
    name: 'Contracts',
    icon: <FileText size={20} />,
    color: '#7486d6',
    items: [
      { id: 'contract-management', name: 'Contract Management', icon: <FileText size={18} />, path: '/contracts/management', available: true },
      { id: 'contract-lifecycle', name: 'Contract Lifecycle', icon: <FileBarChart size={18} />, path: '/contracts/lifecycle', available: true, subtitle: 'drafting, review, approval, execution' },
      { id: 'contract-templates', name: 'Contract Templates', icon: <FileCheck size={18} />, path: '/contracts/templates', available: true, subtitle: 'standardized templates & version control' },
      { id: 'e-signature', name: 'E-Signature', icon: <CheckCircle size={18} />, path: '/contracts/e-signature', available: true, subtitle: 'DocuSign, Adobe Sign integration' },
      { id: 'renewal-alerts', name: 'Renewals & Alerts', icon: <AlertTriangle size={18} />, path: '/contracts/renewals-alerts', available: true, subtitle: 'automated expiry & renewal reminders' },
      { id: 'compliance-tracking', name: 'Compliance Tracking', icon: <Shield size={18} />, path: '/contracts/compliance', available: true, subtitle: 'obligation & compliance monitoring' },
      { id: 'contract-value-reports', name: 'Contract Value Reports', icon: <DollarSign size={18} />, path: '/insights/contractvalue', available: true, subtitle: 'contract value & liability tracking' },
    ],
  },

  // 4. Projects
  {
    id: 'projects',
    name: 'Projects',
    icon: <FolderOpen size={20} />,
    color: '#94cee4',
    items: [
      { id: 'project-management', name: 'Project Management', icon: <FolderOpen size={18} />, path: '/projects/management', available: true, subtitle: 'create, track & manage projects' },
      { id: 'task-management', name: 'Task Management', icon: <Target size={18} />, path: '/tasks/management', available: true, subtitle: 'assign, track & manage tasks' },
      { id: 'team-collaboration', name: 'Team Collaboration', icon: <Users size={18} />, path: '/team/management', available: true, subtitle: 'team management & collaboration' },
      { id: 'project-dashboard', name: 'Project Dashboard', icon: <BarChart3 size={18} />, path: '/projects/dashboard', available: true, subtitle: 'project overview & analytics' },
      { id: 'milestone-tracking', name: 'Milestone Tracking', icon: <CheckCircle size={18} />, path: '/projects/milestones', available: true, subtitle: 'track milestones & deliverables' },
      { id: 'risk-management', name: 'Risk Management', icon: <AlertTriangle size={18} />, path: '/projects/risks', available: true, subtitle: 'identify & manage project risks' },
      { id: 'time-tracking', name: 'Time Tracking', icon: <Clock size={18} />, path: '/projects/time-tracking', available: true, subtitle: 'track time spent on projects' },
      { id: 'project-reports', name: 'Project Reports', icon: <FileText size={18} />, path: '/projects/reports', available: true, subtitle: 'comprehensive project reports' },
      { id: 'workflow-management', name: 'Workflow Management', icon: <Workflow size={18} />, path: '/workflow/management', available: true, subtitle: 'manage & monitor workflows' },
      { id: 'visual-workflow-builder', name: 'Visual Workflow Builder', icon: <Target size={18} />, path: '/workflow/builder', available: true, subtitle: 'drag & drop workflow designer' },
      { id: 'approval-rules-setup', name: 'Approval Rules Setup', icon: <CheckCircle size={18} />, path: '/workflow/rules', available: true, subtitle: 'configure approval workflows' },
      { id: 'workflow-templates', name: 'Workflow Templates', icon: <FileText size={18} />, path: '/workflow/templates', available: true, subtitle: 'pre-built workflow templates' },
      { id: 'workflow-reports', name: 'Workflow Reports', icon: <BarChart3 size={18} />, path: '/workflow/reports', available: true, subtitle: 'workflow performance & analytics' },
    ],
  },

  // 5. Subcontracting
  {
    id: 'subcontracting',
    name: 'Subcontracting',
    icon: <Building size={20} />,
    color: '#7486d6',
    items: [
      { id: 'available-rfps', name: 'Available RFPs', icon: <ClipboardList size={18} />, path: '/subcontracting/rfps', available: true, subtitle: 'browse & bid on open RFPs' },
      { id: 'my-bids', name: 'My Bids', icon: <Send size={18} />, path: '/subcontracting/rfps/my-bids', available: true, subtitle: 'view submitted proposals' },
      { id: 'subcontractor-management', name: 'Subcontractor Management', icon: <Users size={18} />, path: '/subcontracting/management', available: true, subtitle: 'manage subcontractor profiles & performance' },
      { id: 'work-orders', name: 'Work Orders', icon: <FileText size={18} />, path: '/subcontracting/work-orders', available: true, subtitle: 'create, assign & track work orders' },
      { id: 'vendor-onboarding', name: 'Vendor Onboarding', icon: <UserCheck size={18} />, path: '/subcontracting/onboarding', available: true, subtitle: 'streamlined vendor registration process' },
      { id: 'performance-tracking', name: 'Performance Tracking', icon: <TrendingUp size={18} />, path: '/subcontracting/performance', available: true, subtitle: 'monitor performance & quality metrics' },
      { id: 'compliance-management', name: 'Compliance Management', icon: <Shield size={18} />, path: '/subcontracting/compliance', available: true, subtitle: 'ensure regulatory compliance' },
      { id: 'billing-payments', name: 'Billing & Payments', icon: <DollarSign size={18} />, path: '/subcontracting/billing', available: true, subtitle: 'manage billing & payment processing' },
      { id: 'resource-allocation', name: 'Resource Allocation', icon: <Target size={18} />, path: '/subcontracting/resources', available: true, subtitle: 'optimize resource allocation & planning' },
      { id: 'reports-analytics', name: 'Reports & Analytics', icon: <BarChart3 size={18} />, path: '/subcontracting/reports', available: true, subtitle: 'comprehensive reports & analytics' },
    ],
  },

  // 6. Assets
  {
    id: 'assets',
    name: 'Assets',
    icon: <Package size={20} />,
    color: '#94cee4',
    items: [
      { id: 'asset-management', name: 'Asset Management', icon: <Package size={18} />, path: '/assets/management', available: true },
      { id: 'assignment-usage', name: 'Assignment & Usage', icon: <Users size={18} />, path: '/assets/assignment', available: true, subtitle: 'check-in/check-out, allocation tracking' },
      { id: 'maintenance-ops', name: 'Maintenance & Ops', icon: <Wrench size={18} />, path: '/assets/maintenance', available: true, subtitle: 'preventive & corrective maintenance' },
      { id: 'financial-valuation', name: 'Financial & Valuation', icon: <DollarSign size={18} />, path: '/assets/financial', available: true, subtitle: 'depreciation, valuation, insurance' },
      { id: 'asset-lifecycle', name: 'Asset Lifecycle', icon: <Clock size={18} />, path: '/assets/lifecycle', available: true, subtitle: 'acquisition to disposal tracking' },
      { id: 'asset-categorization', name: 'Asset Categorization', icon: <Target size={18} />, path: '/assets/categorization', available: true, subtitle: 'IT, equipment, real estate, licenses' },
      { id: 'asset-roi-reports', name: 'Asset ROI Reports', icon: <Target size={18} />, path: '/insights/assetroi', available: true, subtitle: 'ROI, TCO & utilization reports' },
    ],
  },

  // 7. Finance
  {
    id: 'finance',
    name: 'Finance',
    icon: <Calculator size={20} />,
    color: '#7486d6',
    items: [
      { id: 'asset-finance', name: 'Asset Finance', icon: <DollarSign size={18} />, path: '/finance/assets', available: true, subtitle: 'capitalization, depreciation, valuation' },
      { id: 'contract-finance', name: 'Contract Finance', icon: <Receipt size={18} />, path: '/finance/contracts', available: true, subtitle: 'contract value, billing, payments' },
      { id: 'invoice-validation', name: 'Invoice Validation', icon: <FileCheck size={18} />, path: '/finance/invoices', available: true, subtitle: 'automated invoice processing & validation' },
      { id: 'cashflow-forecasting', name: 'Cash Flow Forecasting', icon: <TrendingUp size={18} />, path: '/finance/cashflow', available: true, subtitle: 'predictive cash flow analysis' },
      { id: 'spend-optimization', name: 'Spend Optimization', icon: <Target size={18} />, path: '/finance/optimization', available: true, subtitle: 'cost reduction & optimization' },
      { id: 'risk-exposure', name: 'Risk & Exposure', icon: <AlertTriangle size={18} />, path: '/finance/risk', available: true, subtitle: 'financial risk assessment' },
      { id: 'multi-currency', name: 'Multi-Currency', icon: <Globe size={18} />, path: '/finance/currency', available: true, subtitle: 'multi-currency support & conversion' },
      { id: 'budgeting-forecasting', name: 'Budgeting & Forecasting', icon: <Calculator size={18} />, path: '/finance/budgeting', available: true, subtitle: 'budget planning & financial forecasting' },
    ],
  },

  // 8. AI Intelligence
  {
    id: 'intelligence',
    name: 'AI Intelligence',
    icon: <Brain size={20} />,
    color: '#94cee4',
    items: [
      { id: 'ai-chat-assistant', name: 'AI Chat Assistant', icon: <MessageCircle size={18} />, path: '/ai/chat-assistant', available: true, subtitle: 'intelligent chat & smart suggestions' },
      { id: 'ai-insights', name: 'AI Insights', icon: <Brain size={18} />, path: '/ai/insights', available: true, subtitle: 'summaries, risk scoring, anomaly detection' },
      { id: 'ai-contract-drafting', name: 'AI Contract Drafting', icon: <FileText size={18} />, path: '/ai/drafting', available: true, subtitle: 'smart contract drafting from prompts' },
      { id: 'ai-search-qa', name: 'AI Search & Q&A', icon: <Search size={18} />, path: '/ai/search', available: true, subtitle: 'natural language search & queries' },
      { id: 'predictive-analytics', name: 'Predictive Analytics', icon: <TrendingUp size={18} />, path: '/ai/analytics', available: true, subtitle: 'renewal & dispute predictions' },
      { id: 'document-ingestion', name: 'Document Ingestion', icon: <FileCheck size={18} />, path: '/ai/ingestion', available: true, subtitle: 'OCR + NLP metadata extraction' },
      { id: 'clause-risk-scoring', name: 'Clause Risk Scoring', icon: <AlertTriangle size={18} />, path: '/ai/risk-scoring', available: true, subtitle: 'AI-powered risk analysis' },
      { id: 'voice-to-contract', name: 'Voice-to-Contract', icon: <Mic size={18} />, path: '/ai/voice-contract', available: true, subtitle: 'speech to contract generation' },
      { id: 'multilingual-translation', name: 'Multilingual Translation', icon: <Languages size={18} />, path: '/ai/translation', available: true, subtitle: 'auto-translate contracts' },
      { id: 'ai-negotiation', name: 'AI Negotiation Support', icon: <Users2 size={18} />, path: '/ai/negotiation', available: true, subtitle: 'clause suggestions & strategies' },
    ],
  },

  // 9. Collaboration
  {
    id: 'collaboration',
    name: 'Collaboration',
    icon: <MessageSquare size={20} />,
    color: '#7486d6',
    items: [
      { id: 'collaboration-hub', name: 'Collaboration Hub', icon: <MessageSquare size={18} />, path: '/collaboration/hub', available: true, subtitle: 'messaging & task assignments' },
      { id: 'version-control', name: 'Version Control', icon: <FileCheck size={18} />, path: '/collaboration/versioning', available: true, subtitle: 'document versioning & history' },
      { id: 'audit-trails', name: 'Audit Trails', icon: <Clock size={18} />, path: '/collaboration/audit', available: true, subtitle: 'complete edit & approval history' },
      { id: 'access-controls', name: 'Access Controls', icon: <UserCheck size={18} />, path: '/collaboration/access', available: true, subtitle: 'granular permissions & roles' },
      { id: 'compliance-reports', name: 'Compliance Reports', icon: <Shield size={18} />, path: '/collaboration/compliance', available: true, subtitle: 'real-time KPIs & compliance' },
      { id: 'document-library', name: 'Document Library', icon: <FileText size={18} />, path: '/collaboration/documents', available: true, subtitle: 'centralized document management' },
    ],
  },

  // 10. Integration
  {
    id: 'integration',
    name: 'Integration',
    icon: <Link size={20} />,
    color: '#94cee4',
    items: [
      { id: 'erp-integration', name: 'ERP Integration', icon: <Database size={18} />, path: '/integration/erp', available: true, subtitle: 'SAP, Oracle, NetSuite sync' },
      { id: 'procurement-integration', name: 'Procurement Integration', icon: <Package size={18} />, path: '/integration/procurement', available: true, subtitle: 'purchase contract linking' },
      { id: 'crm-integration', name: 'CRM Integration', icon: <Users size={18} />, path: '/integration/crm', available: true, subtitle: 'Salesforce, HubSpot integration' },
      { id: 'sso-access-control', name: 'SSO & Access Control', icon: <Lock size={18} />, path: '/integration/sso', available: true, subtitle: 'LDAP, AD, SSO integration' },
      { id: 'api-management', name: 'API Management', icon: <Zap size={18} />, path: '/integration/api', available: true, subtitle: 'REST APIs & webhooks' },
    ],
  },

  // 11. System
  {
    id: 'system',
    name: 'System',
    icon: <Settings size={20} />,
    color: '#7486d6',
    items: [
      { id: 'settings', name: 'Settings', icon: <Cog size={18} />, path: '/system/settings', available: true, subtitle: 'system configuration & preferences' },
      { id: 'master-data', name: 'Master Data', icon: <Database size={18} />, path: '/system/master-data', available: true, subtitle: 'dropdown values & configuration' },
      { id: 'vendor-management', name: 'Vendor Management', icon: <Building size={18} />, path: '/vendors', available: true, subtitle: 'vendor & supplier management' },
      { id: 'customer-management', name: 'Customer Management', icon: <Users2 size={18} />, path: '/customers', available: true, subtitle: 'customer & client management' },
      { id: 'clause-library', name: 'Clause Library', icon: <FileText size={18} />, path: '/system/clause-library', available: true, subtitle: 'legal clauses with AI features' },
      { id: 'user-management', name: 'User Management', icon: <Users size={18} />, path: '/system/users', available: true, subtitle: 'user accounts & permissions' },
      { id: 'role-management', name: 'Role Management', icon: <UserCheck size={18} />, path: '/system/roles', available: true, subtitle: 'roles & access control' },
      { id: 'department-management', name: 'Department Management', icon: <Building2 size={18} />, path: '/system/departments', available: true, subtitle: 'organizational departments' },
      { id: 'system-monitoring', name: 'System Monitoring', icon: <Monitor size={18} />, path: '/system/monitoring', available: true, subtitle: 'system health & performance' },
      { id: 'backup-restore', name: 'Backup & Restore', icon: <Database size={18} />, path: '/system/backup', available: false, subtitle: 'data backup & recovery' },
      { id: 'security-settings', name: 'Security Settings', icon: <Shield size={18} />, path: '/system/security', available: false, subtitle: 'security policies & compliance' },
      { id: 'mobile-app-access', name: 'Mobile App Access', icon: <Smartphone size={18} />, path: '/system/mobile', available: true, subtitle: 'mobile & PWA configuration' },
    ],
  },

  // 12. Support
  {
    id: 'support',
    name: 'Support',
    icon: <Headphones size={20} />,
    color: '#94cee4',
    items: [],
  },
];

  const groupIdToAssignedPath = {
    overview: '/overview',
    sourcing: '/sourcing',
    contracts: '/contracts',
    projects: '/projects',
    subcontracting: '/subcontracting',
    assets: '/assets',
    finance: '/finance',
    intelligence: '/ai',
    collaboration: '/collaboration',
    integration: '/integration',
    system: '/system',
    support: '/support',
  };

  const assignedMenus = Array.isArray(user?.assignedMenus) ? user.assignedMenus : [];
  const hasMenuRestriction = assignedMenus.length > 0;

  const displayedMenuGroups = hasMenuRestriction
    ? menuGroups.filter((g) => {
        const mapped = groupIdToAssignedPath[g.id];
        if (!mapped) return true;
        return assignedMenus.includes(mapped);
      })
    : menuGroups;

  const toggleGroup = (groupId) => {
    const landingPageMap = {
      overview: '/overview',
      assets: '/assets',
      sourcing: '/sourcing',
      contracts: '/contracts',
      finance: '/finance',
      intelligence: '/ai',
      projects: '/projects',
      subcontracting: '/subcontracting',
      collaboration: '/collaboration',
      integration: '/integration',
      system: '/system',
      support: '/support',
    };

    if (groupId === 'intelligence' && !aiEnabled) return;

    if (landingPageMap[groupId]) {
      onNavigate(landingPageMap[groupId]);
      return;
    }

    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleItemClick = (item) => {
    if (item.path && item.path.startsWith('/ai') && !aiEnabled) {
      return;
    }
    if (item.available) {
      onNavigate(item.path);
    } else {
      onNavigate(
        `/coming-soon?feature=${item.id}&name=${encodeURIComponent(item.name)}`,
      );
    }
  };

  const getActiveGroup = () => {
    const landingPageMap = {
      '/overview': 'overview',
      '/assets': 'assets',
      '/sourcing': 'sourcing',
      '/contracts': 'contracts',
      '/finance': 'finance',
      '/intelligence': 'intelligence',
      '/ai': 'intelligence',
      '/system': 'system',
      '/projects': 'projects',
      '/subcontracting': 'subcontracting',
      '/collaboration': 'collaboration',
      '/integration': 'integration',
      '/support': 'support',
    };

    if (landingPageMap[activePage]) {
      return landingPageMap[activePage];
    }

    if (activePage.startsWith('/assets/')) return 'assets';
    if (activePage.startsWith('/sourcing/')) return 'sourcing';
    if (activePage.startsWith('/contracts/')) return 'contracts';
    if (activePage.startsWith('/ai/')) return 'intelligence';
    if (activePage.startsWith('/finance/')) return 'finance';
    if (activePage.startsWith('/workflow/')) return 'projects';
    if (activePage.startsWith('/insights/')) return 'intelligence';
    if (activePage.startsWith('/collaboration/')) return 'collaboration';
    if (activePage.startsWith('/integration/')) return 'integration';
    if (activePage.startsWith('/system/')) return 'system';
    if (activePage.startsWith('/projects/')) return 'projects';
    if (activePage.startsWith('/subcontracting/')) return 'subcontracting';
    if (activePage.startsWith('/tasks/')) return 'projects';

    return (
      menuGroups.find((group) =>
        group.items.some((item) => item.path === activePage),
      )?.id || 'overview'
    );
  };

  const isGroupActive = (groupId) => {
    return getActiveGroup() === groupId;
  };

  const { aiEnabled } = useAI();

  return (
    <div
      style={{
        width: isOpen ? '280px' : '0',
        height: '100vh',
        backgroundColor: SIDEBAR_THEME.bg,
        borderRight: `1px solid ${SIDEBAR_THEME.borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Noto Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        boxShadow: '2px 0 12px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        overflowX: 'hidden',
        transition: 'width 0.3s ease',
        opacity: isOpen ? 1 : 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${SIDEBAR_THEME.borderColor}`,
          backgroundColor: SIDEBAR_THEME.bg,
          height: '70px',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minWidth: 0,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              padding: '2px',
            }}
          >
            <CAM360Badge size={40} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#FFFFFF',
                lineHeight: '1.3',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              CLM
            </div>
            <div
              style={{
                fontSize: '11px',
                color: SIDEBAR_THEME.textMuted,
                lineHeight: '1.3',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Contract LifeCycle Management
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <div
        className="enterprise-nav-menu"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '2px 0',
        }}
      >
        {displayedMenuGroups.map((group, index) => {
          const active = isGroupActive(group.id);
          const isAiDisabled = !aiEnabled && group.id === 'intelligence';

          return (
            <div key={group.id} style={{ marginBottom: '2px' }}>
              {/* Group Header */}
              <button
                onClick={isAiDisabled ? undefined : () => toggleGroup(group.id)}
                title={isAiDisabled ? 'AI is disabled. Enable AI from the top navigation.' : undefined}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                  marginBottom: '0px',
                  minWidth: 0,
                  backgroundColor: active ? SIDEBAR_THEME.activeBg : 'transparent',
                  border: 'none',
                  cursor: isAiDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: active ? '600' : '400',
                  color: isAiDisabled
                    ? SIDEBAR_THEME.disabledColor
                    : active
                      ? '#FFFFFF'
                      : SIDEBAR_THEME.textColor,
                  transition: 'all 0.15s ease-in-out',
                  fontFamily: 'Noto Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  borderLeft: active
                    ? `3px solid ${SIDEBAR_THEME.activeColor}`
                    : '3px solid transparent',
                  marginLeft: active ? '-3px' : '0',
                  borderRadius: '0',
                  opacity: isAiDisabled ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!active && !isAiDisabled) {
                    e.currentTarget.style.backgroundColor = SIDEBAR_THEME.hoverBg;
                    e.currentTarget.style.color = '#FFFFFF';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active && !isAiDisabled) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = SIDEBAR_THEME.textColor;
                  }
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    minWidth: 0,
                    flex: 1,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      color: isAiDisabled
                        ? SIDEBAR_THEME.disabledColor
                        : active
                          ? group.color
                          : SIDEBAR_THEME.textMuted,
                      flexShrink: 0,
                      transition: 'color 0.15s ease',
                    }}
                  >
                    {group.icon}
                  </div>
                  <span
                    style={{
                      fontFamily: 'Noto Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                  >
                    {group.name}
                  </span>
                </div>
              </button>

              {/* Subtle separator */}
              {index < menuGroups.length - 1 && (
                <div
                  style={{
                    height: '1px',
                    background: SIDEBAR_THEME.separatorGradient,
                    width: '100%',
                    marginTop: '2px',
                    marginLeft: '16px',
                    marginRight: '16px',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '10px 16px',
          borderTop: `1px solid ${SIDEBAR_THEME.borderColor}`,
          backgroundColor: SIDEBAR_THEME.footerBg,
        }}
      >
        {/* User Info */}
        {user && (
          <div
            style={{
              fontSize: '11px',
              color: SIDEBAR_THEME.activeColor,
              textAlign: 'center',
              lineHeight: '1.4',
              marginBottom: '4px',
              fontWeight: '500',
            }}
          >
            {user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email || 'User'}{' '}
            {user.role?.displayName || user.role?.name
              ? `(${user.role.displayName || user.role.name})`
              : ''}
          </div>
        )}
        {user?.company?.companyName && (
          <div
            style={{
              fontSize: '10px',
              color: SIDEBAR_THEME.textMuted,
              textAlign: 'center',
              lineHeight: '1.4',
              marginBottom: '4px',
            }}
          >
            {user.company.companyName}
            {user.company.companyId ? ` · ${user.company.companyId}` : ''}
          </div>
        )}
        {/* Version Info */}
        <div
          style={{
            fontSize: '11px',
            color: SIDEBAR_THEME.textMuted,
            textAlign: 'center',
            lineHeight: '1.4',
          }}
        >
          CAM360 v1.0
          <br />
          Contract LifeCycle Management
        </div>
      </div>
    </div>
  );
};

export default EnterpriseNavigation;